/**
 * Knowledge review: confirm, edit, reject or dispute proposed facts.
 * Nothing here is automatic — this screen is the only path to `verified`.
 */

import { useState } from "react";

import { knowledge as knowledgeApi } from "../api/resources";
import { enterpriseContext } from "../api/resources";
import { GovernanceBadge } from "../components/GovernanceBadge";
import {
  KnowledgeLegend,
  KnowledgeStatusBadges,
} from "../components/KnowledgeStatus";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHeader,
  Stat,
  Tabs,
  Triple,
} from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import {
  confidence,
  dateTime,
  predicateLabel,
  relativeTime,
} from "../lib/format";
import { useAsync } from "../lib/useAsync";
import type { FactDetail, KnowledgeFact } from "../lib/types";

type Filter = "proposed" | "verified" | "disputed" | "rejected" | "all";

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: "proposed", label: "Needs confirmation" },
  { value: "verified", label: "Human confirmed" },
  { value: "disputed", label: "Disputed" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const STATUS_PARAM: Record<Filter, string | undefined> = {
  proposed: "proposed,claimed",
  verified: "verified",
  disputed: "disputed",
  rejected: "rejected,superseded",
  all: undefined,
};

export function KnowledgePage() {
  const { organizationId } = useCurrentOrganization();
  const [filter, setFilter] = useState<Filter>("proposed");
  const [search, setSearch] = useState("");
  const [actor, setActor] = useState(
    () => localStorage.getItem("discovery.actor") ?? "",
  );
  const [selected, setSelected] = useState<FactDetail | undefined>();
  const [editing, setEditing] = useState<KnowledgeFact | undefined>();
  const [editValue, setEditValue] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();

  const facts = useAsync(
    () =>
      organizationId
        ? knowledgeApi.facts(organizationId, {
            verification_status: STATUS_PARAM[filter],
            q: search || undefined,
            limit: 200,
          })
        : Promise.resolve({ items: [], total: 0, limit: 0, offset: 0 }),
    [organizationId, filter, search],
  );

  // What Enterprise Context did with the observations we proposed — one read, mapped by fact.
  // Separate from the human-confirmation badge on purpose: two decisions, two systems.
  const exports = useAsync(
    () =>
      organizationId
        ? enterpriseContext.exports(organizationId)
        : Promise.resolve([]),
    [organizationId, facts.data],
  );
  const exportByFact = new Map(
    (exports.data ?? []).map((row) => [row.fact_id, row]),
  );
  const stats = useAsync(
    () =>
      organizationId
        ? knowledgeApi.stats(organizationId)
        : Promise.resolve({
            by_verification_status: {} as Record<string, number>,
            by_subject_type: {} as Record<string, number>,
            by_source_type: {} as Record<string, number>,
          }),
    [organizationId, facts.data],
  );

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" />
      </Card>
    );
  }

  const rememberActor = (value: string) => {
    setActor(value);
    localStorage.setItem("discovery.actor", value);
  };

  async function act(
    fact: KnowledgeFact,
    action: "confirm" | "reject" | "dispute",
  ) {
    setError(undefined);
    try {
      await knowledgeApi[action](organizationId!, fact.id, {
        actor: actor || undefined,
      });
      facts.reload();
      setSelected(undefined);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function saveEdit() {
    if (!editing) return;
    setError(undefined);
    try {
      await knowledgeApi.edit(organizationId!, editing.id, {
        object_value: editValue,
        actor: actor || undefined,
        reason: reason || undefined,
        verify: true,
      });
      setEditing(undefined);
      setReason("");
      facts.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function openDetail(fact: KnowledgeFact) {
    setError(undefined);
    try {
      setSelected(await knowledgeApi.fact(organizationId!, fact.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const byStatus = stats.data?.by_verification_status ?? {};
  const rows = facts.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="What we learned"
        description="Observations: what people told us and what documents showed. A person here confirms what was heard; only Enterprise Context decides what becomes official — that second badge says where each observation stands."
        actions={
          <input
            style={{ width: 190 }}
            placeholder="Your name (for the audit trail)"
            value={actor}
            onChange={(event) => rememberActor(event.target.value)}
          />
        }
      />

      <div className="stack">
        <ErrorBanner error={facts.error ?? error} />

        <KnowledgeLegend />

        <div className="grid grid--stats">
          <Stat
            label="Needs confirmation"
            value={(byStatus.proposed ?? 0) + (byStatus.claimed ?? 0)}
            tone="warn"
          />
          <Stat
            label="Human confirmed"
            value={byStatus.verified ?? 0}
            tone="ok"
          />
          <Stat label="Conflicting" value={byStatus.disputed ?? 0} />
          <Stat
            label="Rejected"
            value={(byStatus.rejected ?? 0) + (byStatus.superseded ?? 0)}
          />
        </div>

        <Card
          flush
          title={
            <Tabs
              value={filter}
              onChange={setFilter}
              options={FILTERS.map((row) => ({
                value: row.value,
                label: row.label,
              }))}
            />
          }
          actions={
            <input
              style={{ width: 220 }}
              placeholder="Search subject, predicate or value"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          }
        >
          {facts.loading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing here"
              description="Run an interview or upload a document, then run the knowledge pipeline from the onboarding page."
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fact</th>
                    <th>Source</th>
                    <th className="numeric">Confidence</th>
                    <th>Status</th>
                    <th style={{ width: 250 }}>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((fact) => (
                    <tr key={fact.id}>
                      <td>
                        <Triple
                          subject={fact.subject_name}
                          predicate={predicateLabel(fact.predicate)}
                          object={fact.object_value}
                        />
                        <div className="tiny faint">
                          {fact.subject_type} · {relativeTime(fact.created_at)}
                        </div>
                      </td>
                      <td className="small muted">
                        <div>{fact.source_type.replace(/_/g, " ")}</div>
                        <div className="tiny faint truncate">
                          {fact.evidence_reference}
                        </div>
                      </td>
                      <td className="numeric">{confidence(fact.confidence)}</td>
                      <td>
                        <div
                          className="row"
                          style={{ gap: 4, flexWrap: "wrap" }}
                        >
                          <KnowledgeStatusBadges fact={fact} />
                          <GovernanceBadge export={exportByFact.get(fact.id)} />
                        </div>
                      </td>
                      <td>
                        <div className="row" style={{ gap: 5 }}>
                          <button
                            className="btn--small btn--ok"
                            onClick={() => void act(fact, "confirm")}
                          >
                            Confirm
                          </button>
                          <button
                            className="btn--small"
                            onClick={() => {
                              setEditing(fact);
                              setEditValue(fact.object_value ?? "");
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn--small btn--danger"
                            onClick={() => void act(fact, "reject")}
                          >
                            Reject
                          </button>
                          <button
                            className="btn--small"
                            onClick={() => void act(fact, "dispute")}
                          >
                            Dispute
                          </button>
                          <button
                            className="btn--small btn--ghost"
                            onClick={() => void openDetail(fact)}
                          >
                            Evidence
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {editing ? (
        <Modal
          title="Edit fact"
          onClose={() => setEditing(undefined)}
          footer={
            <>
              <button onClick={() => setEditing(undefined)}>Cancel</button>
              <button className="primary" onClick={() => void saveEdit()}>
                Save &amp; verify
              </button>
            </>
          }
        >
          <p className="small muted">
            The original extracted value is preserved in the audit trail —
            editing never overwrites what was actually said.
          </p>
          <Triple
            subject={editing.subject_name}
            predicate={predicateLabel(editing.predicate)}
            object={editing.object_value}
          />
          <div style={{ height: 14 }} />
          <Field label="Corrected value">
            <input
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
            />
          </Field>
          <Field label="Reason" hint="Why the original was wrong">
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        </Modal>
      ) : null}

      {selected ? (
        <Modal
          title="Evidence & history"
          onClose={() => setSelected(undefined)}
        >
          <Triple
            subject={selected.subject_name}
            predicate={predicateLabel(selected.predicate)}
            object={selected.object_value}
          />
          <div className="row small muted" style={{ margin: "10px 0 16px" }}>
            <KnowledgeStatusBadges fact={selected} />
            <GovernanceBadge export={exportByFact.get(selected.id)} />
            <span>confidence {confidence(selected.confidence)}</span>
            <span>extractor {selected.extractor ?? "—"}</span>
            {selected.verified_by ? (
              <span>confirmed by {selected.verified_by}</span>
            ) : null}
          </div>

          <h3>Evidence ({selected.evidence.length})</h3>
          <div className="stack" style={{ gap: 8, margin: "8px 0 18px" }}>
            {selected.evidence.map((row) => (
              <div key={row.id} className="evidence">
                <div className="evidence__ref">{row.reference}</div>
                {row.snippet ? (
                  <div style={{ marginTop: 4 }}>“{row.snippet}”</div>
                ) : null}
                <div className="tiny faint" style={{ marginTop: 4 }}>
                  {row.source_type.replace(/_/g, " ")} · captured{" "}
                  {dateTime(row.captured_at)}
                </div>
              </div>
            ))}
          </div>

          <h3>Audit ({selected.audit.length})</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Actor</th>
                  <th>Reason</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {selected.audit.map((row) => (
                  <tr key={row.id}>
                    <td>{row.action}</td>
                    <td className="small">{row.actor ?? "—"}</td>
                    <td className="small muted truncate">
                      {row.reason ?? "—"}
                    </td>
                    <td className="small muted">{dateTime(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
